/**
 * AURELIA — component-search.js
 * <predictive-search> — the search drawer.
 *
 * Implements the ARIA combobox pattern over a real GET form. The form works on
 * its own; everything here is enhancement:
 *   • debounced /search/suggest requests, rendered by Liquid
 *   • in-flight requests aborted when the query changes
 *   • arrow-key navigation with aria-activedescendant
 *   • result count announced once per query, politely
 */
(function () {
  'use strict';

  const { Drawer, utils, config } = window.Aurelia;

  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_MS = 200;

  class PredictiveSearch extends Drawer {
    setup() {
      this.input = this.$('[data-search-input]');
      this.form = this.$('[data-search-form]');
      this.results = this.$('[data-search-results]');
      this.idle = this.$('[data-search-idle]');
      this.loading = this.$('[data-search-loading]');
      this.status = this.$('[data-search-status]');
      this.clearButton = this.$('[data-search-clear]');

      if (!this.input) return;

      this.enabled = this.dataset.enabled === 'true';
      this.activeIndex = -1;
      this.options = [];

      this.search = utils.debounce(() => this.fetchResults(), DEBOUNCE_MS);

      this.listen(this.input, 'input', () => this.handleInput());
      this.listen(this.input, 'keydown', (event) => this.handleKeydown(event));

      if (this.clearButton) {
        this.listen(this.clearButton, 'click', () => this.clear());
      }

      // Submitting with a highlighted suggestion should follow that suggestion
      // rather than running a generic search for the typed text.
      this.listen(this.form, 'submit', (event) => {
        const active = this.options[this.activeIndex];
        if (!active) return;
        const link = active.matches('a') ? active : active.querySelector('a');
        if (!link) return;
        event.preventDefault();
        window.location.href = link.href;
      });
    }

    onClose() {
      this.reset();
    }

    handleInput() {
      const value = this.input.value.trim();

      if (this.clearButton) this.clearButton.hidden = value.length === 0;

      if (!this.enabled) return;

      if (value.length < MIN_QUERY_LENGTH) {
        this.search.cancel();
        this.abort();
        this.reset();
        return;
      }

      this.search();
    }

    handleKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.move(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.move(-1);
          break;
        case 'Home':
          if (this.options.length) {
            event.preventDefault();
            this.highlight(0);
          }
          break;
        case 'End':
          if (this.options.length) {
            event.preventDefault();
            this.highlight(this.options.length - 1);
          }
          break;
        case 'Escape':
          // First Escape clears the query; a second closes the drawer. The
          // drawer's own Escape handler covers the second press.
          if (this.input.value) {
            event.stopPropagation();
            this.clear();
          }
          break;
        default:
          break;
      }
    }

    move(direction) {
      if (this.options.length === 0) return;
      const next = this.activeIndex + direction;

      if (next < 0) {
        this.highlight(-1);
        return;
      }

      this.highlight(next % this.options.length);
    }

    highlight(index) {
      this.options.forEach((option) => option.classList.remove('is-highlighted'));
      this.activeIndex = index;

      if (index < 0) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }

      const option = this.options[index];
      option.classList.add('is-highlighted');
      option.scrollIntoView({ block: 'nearest' });
      this.input.setAttribute('aria-activedescendant', option.id);
    }

    abort() {
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    async fetchResults() {
      const terms = this.input.value.trim();
      if (terms.length < MIN_QUERY_LENGTH) return;

      this.abort();
      this.controller = new AbortController();

      if (this.loading) this.loading.hidden = false;

      const url = new URL(config.routes.predictive_search_url, window.location.origin);
      url.searchParams.set('q', terms);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('resources[limit]', String(this.dataset.limit || 6));
      url.searchParams.set('resources[limit_scope]', 'each');

      const types = [];
      if (this.dataset.showProducts !== 'false') types.push('product');
      if (this.dataset.showCollections !== 'false') types.push('collection');
      if (this.dataset.showPages !== 'false') types.push('page');
      if (this.dataset.showArticles === 'true') types.push('article');
      if (this.dataset.showQueries !== 'false') types.push('query');
      url.searchParams.set('resources[type]', types.join(','));

      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(`Search failed: ${response.status}`);

        const html = await response.text();
        const fragment = utils.parseHTML(html).querySelector('.search-results');
        if (!fragment) return;

        this.render(fragment);
      } catch (error) {
        // AbortError is the expected outcome of typing another character.
        if (error.name === 'AbortError') return;
        this.reset();
      } finally {
        if (this.loading) this.loading.hidden = true;
      }
    }

    render(fragment) {
      this.results.innerHTML = '';
      this.results.appendChild(fragment);
      this.results.hidden = false;
      if (this.idle) this.idle.hidden = true;

      this.input.setAttribute('aria-expanded', 'true');

      this.options = Array.from(this.results.querySelectorAll('[data-search-option]'));
      this.highlight(-1);

      const total = Number(fragment.dataset.resultTotal) || 0;
      this.announce(total);
    }

    announce(total) {
      if (!this.status) return;
      const template = total === 1 ? this.dataset.statusOne : this.dataset.statusMany;
      if (!template) {
        this.status.textContent = String(total);
        return;
      }
      // utils.interpolate is a deferred module; the live region updating a
      // microtask later is not observable.
      utils.interpolate(template, { count: total }).then((text) => {
        this.status.textContent = text;
      });
    }

    clear() {
      this.input.value = '';
      if (this.clearButton) this.clearButton.hidden = true;
      this.abort();
      this.reset();
      this.input.focus();
    }

    reset() {
      if (this.results) {
        this.results.innerHTML = '';
        this.results.hidden = true;
      }
      if (this.idle) this.idle.hidden = false;
      if (this.status) this.status.textContent = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.options = [];
      this.activeIndex = -1;
    }
  }

  window.Aurelia.define('predictive-search', PredictiveSearch);

  /* ---------------------------------------------------------------------
     Inline search form (search results page)
     Clearing the field must also clear the URL so a refresh does not
     resurrect the old query.
     --------------------------------------------------------------------- */
  document.querySelectorAll('[data-search-page-form]').forEach((form) => {
    const clear = form.querySelector('[data-search-clear]');
    const input = form.querySelector('input[name="q"]');
    if (!clear || !input) return;

    clear.hidden = input.value.length === 0;
    input.addEventListener('input', () => {
      clear.hidden = input.value.length === 0;
    });
    clear.addEventListener('click', () => {
      input.value = '';
      clear.hidden = true;
      input.focus();
    });
  });
})();
